import {
  HomeIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  PuzzlePieceIcon,
  UsersIcon,
  CogIcon,
  CreditCardIcon,
  FunnelIcon,
  CpuChipIcon,
  ChartBarIcon,
  EyeIcon,
  BriefcaseIcon,
  DocumentDuplicateIcon,
  CalculatorIcon,
} from 'components/icons';

export type PlanGate = 'STANDARD' | 'PRO' | 'ENTERPRISE';

export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  plan?: PlanGate;
  /** Shown in the mobile bottom bar. Keep to four — it's a tab bar, not a menu. */
  primary?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Grouped along the product's own story: Find → Pursue → Company → Insights.
 * Previously this was a flat list of fifteen items that scrolled on a laptop
 * and gave no sense of which stage of the workflow anything belonged to.
 *
 * Icons are intentionally uncolored here. Eight different icon hues in one
 * vertical list is noise; color now signals active state and plan locks only.
 */
export const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, primary: true },
    ],
  },
  {
    label: 'Find',
    items: [
      { name: 'Opportunities', href: '/opportunities', icon: MagnifyingGlassIcon, primary: true },
      { name: 'Watch List', href: '/watchlist', icon: EyeIcon },
    ],
  },
  {
    label: 'Pursue',
    items: [
      { name: 'Pursuits', href: '/pipeline', icon: FunnelIcon, primary: true },
      { name: 'Proposal Writer', href: '/proposal-writer', icon: DocumentTextIcon, plan: 'PRO' },
      { name: 'Documents', href: '/documents', icon: FolderIcon, plan: 'STANDARD' },
    ],
  },
  {
    label: 'My Company',
    items: [
      { name: 'Past Performance', href: '/past-performance', icon: BriefcaseIcon },
      { name: 'Boilerplate', href: '/boilerplate', icon: DocumentDuplicateIcon },
      { name: 'Pricing Profile', href: '/pricing-profile', icon: CalculatorIcon },
      { name: 'Team', href: '/team', icon: UsersIcon, plan: 'PRO' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, plan: 'PRO' },
      { name: 'CRM', href: '/crm', icon: ClipboardDocumentListIcon, plan: 'PRO' },
    ],
  },
  {
    label: 'Advanced',
    items: [
      { name: 'AI Assistant', href: '/ai-assistant', icon: ChatBubbleLeftRightIcon, plan: 'PRO' },
      { name: 'AI Actions', href: '/agents', icon: CpuChipIcon, plan: 'PRO' },
      { name: 'Integrations', href: '/integrations', icon: PuzzlePieceIcon, plan: 'PRO' },
    ],
  },
];

export const secondaryNav: NavItem[] = [
  { name: 'Billing', href: '/billing', icon: CreditCardIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

/** Flat view, for lookups. */
export const allNavItems: NavItem[] = navGroups.flatMap((g) => g.items);

/** Mobile tab bar — the four routes that carry the daily loop. */
export const mobileTabs: NavItem[] = allNavItems.filter((i) => i.primary);

/**
 * Plan tier check, independent of navigation. Anything outside the sidebar that
 * gates on plan (in-page buttons, shortcuts) should use this rather than
 * re-deriving the tier order, so a locked nav item and an unlocked button
 * cannot disagree about the same feature.
 */
export function planAllows(required: PlanGate | undefined, userPlan: string): boolean {
  if (!required) return true;
  if (required === 'STANDARD') return ['STANDARD', 'PRO', 'ENTERPRISE'].includes(userPlan);
  if (required === 'PRO') return ['PRO', 'ENTERPRISE'].includes(userPlan);
  if (required === 'ENTERPRISE') return userPlan === 'ENTERPRISE';
  return true;
}

export function canAccess(item: NavItem, userPlan: string): boolean {
  return planAllows(item.plan, userPlan);
}

/**
 * Route → document title. Exact matches first; dynamic routes fall through to
 * the prefix list below. Keeping this next to the nav means adding a route
 * can't silently ship without a tab title.
 */
const exactTitles: Record<string, string> = {
  '/': 'Stop Checking SAM.gov Every Morning',
  '/features': 'How It Works',
  '/pricing': 'Pricing',
  '/about': 'About',
  '/login': 'Sign In',
  '/signup': 'Get Early Access',
  '/forgot-password': 'Reset Your Password',
  '/reset-password': 'Choose a New Password',
  '/verify-email': 'Verify Your Email',
};

const prefixTitles: Array<[string, string]> = [
  ['/opportunities/', 'Opportunity'],
  ['/pursuits/', 'Pursuit'],
];

export function titleForPath(pathname: string): string | null {
  if (exactTitles[pathname]) return exactTitles[pathname];

  const navMatch = [...allNavItems, ...secondaryNav].find((i) => i.href === pathname);
  if (navMatch) return navMatch.name;

  const prefixMatch = prefixTitles.find(([prefix]) => pathname.startsWith(prefix));
  if (prefixMatch) return prefixMatch[1];

  return null;
}
